import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import baseTheme from './index';

export const ThemeModeContext = React.createContext({ mode:'light', toggle: ()=>{} });

export default function ThemeModeProvider({ children }){
  const [mode, setMode] = React.useState(()=> (localStorage.getItem('hrapp.theme') || 'light'));
  const toggle = React.useCallback(()=> setMode(m => {
    const next = m === 'light' ? 'dark' : 'light';
    localStorage.setItem('hrapp.theme', next);
    return next;
  }), []);

  React.useEffect(()=>{
    const cls = 'dark-mode';
    if (mode === 'dark') document.body.classList.add(cls); else document.body.classList.remove(cls);
  }, [mode]);

  const theme = React.useMemo(()=> createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode,
      ...(mode === 'dark' ? {
        background: {
          default: '#0f1115',
          paper: '#12161c'
        },
        text: {
          primary: '#e6eaf2',
          secondary: 'rgba(230,234,242,0.7)'
        },
        divider: 'rgba(255,255,255,0.12)',
        action: {
          hover: 'rgba(255,255,255,0.08)',
          selected: 'rgba(255,255,255,0.12)',
          disabled: 'rgba(255,255,255,0.26)',
          disabledBackground: 'rgba(255,255,255,0.12)'
        }
      } : {
        background: baseTheme.palette.background,
        text: baseTheme.palette.text
      })
    }
  }), [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, toggle }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <style>{`
          body {
            --mui-palette-background-default: ${theme.palette.background.default};
            --mui-palette-background-paper: ${theme.palette.background.paper};
            --mui-palette-text-primary: ${theme.palette.text.primary};
            --mui-palette-text-secondary: ${theme.palette.text.secondary};
            --mui-palette-divider: ${theme.palette.divider};
            --mui-palette-action-hover: ${theme.palette.action.hover};
            --mui-palette-action-selected: ${theme.palette.action.selected};
            --mui-palette-primary-main: ${theme.palette.primary.main};
            --mui-palette-primary-light: ${theme.palette.primary.light};
            --mui-palette-success-main: ${theme.palette.success.main};
            --mui-palette-success-light: ${theme.palette.success.light};
            --mui-palette-success-contrastText: ${theme.palette.getContrastText ? theme.palette.getContrastText(theme.palette.success.main) : '#fff'};
            --mui-palette-info-light: ${theme.palette.info.light};
            --mui-shadows-1: ${Array.isArray(theme.shadows) ? theme.shadows[1] : '0 1px 2px rgba(0,0,0,0.04)'};
          }

          /* Global form focus styles */
          input:focus,
          textarea:focus,
          .MuiInputBase-input:focus,
          .MuiOutlinedInput-input:focus {
            outline: none !important;
          }
          .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-width: 2px !important;
            border-color: var(--mui-palette-primary-main) !important;
          }

          /* Dark mode table fixes */
          ${mode === 'dark' ? `
            .MuiTableCell-root {
              color: ${theme.palette.text.primary} !important;
              border-bottom-color: ${theme.palette.divider} !important;
            }
            .MuiTableHead-root .MuiTableCell-root {
              background-color: ${theme.palette.action.hover} !important;
              color: ${theme.palette.text.primary} !important;
              font-weight: 600 !important;
            }
            .MuiTableRow-root:hover {
              background-color: ${theme.palette.action.hover} !important;
            }
            .MuiPaper-root {
              background-color: ${theme.palette.background.paper} !important;
            }
          ` : ''}
        `}</style>
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

